import { useCallback, useEffect, useRef, useState } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import type { BaseComponent } from '../../types/protocols';
import { checkComponentName } from './useCheckName';

interface CheckComponentRef {
    buildingId?: string;
    componentKey?: string;
}

const refKey = (buildingKey: string, componentKey: string) => `${buildingKey}/${componentKey}`;

/**
 * Names of the components that checks spanning several buildings are about, for
 * `checkDisplayName` on pages without the buildings' models (Förvaltning, Alla aktiviteter).
 * Reads only the referenced component nodes; a name is undefined until loaded or when
 * the component is gone or archived.
 */
export const useCheckComponentNames = (checks: CheckComponentRef[]) => {
    const [names, setNames] = useState<Record<string, string | null>>({});
    // Keys already requested, so a re-render never reads a component twice
    const requested = useRef(new Set<string>());
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        const missing = new Map<string, { buildingKey: string; componentKey: string }>();
        for (const check of checks) {
            if (!check.buildingId || !check.componentKey) continue;
            const key = refKey(check.buildingId, check.componentKey);
            if (!requested.current.has(key)) missing.set(key, { buildingKey: check.buildingId, componentKey: check.componentKey });
        }
        if (missing.size === 0) return;
        missing.forEach((_ref, key) => requested.current.add(key));
        const db = getDatabase();
        Promise.all(Array.from(missing.entries()).map(async ([key, { buildingKey, componentKey }]) => {
            try {
                const snapshot = await get(ref(db, `buildings/${buildingKey}/structure/components/${componentKey}`));
                return [key, checkComponentName(snapshot.exists() ? (snapshot.val() as BaseComponent) : null) ?? null] as const;
            } catch (error) {
                console.error('Error loading component name for check:', error);
                return [key, null] as const;
            }
        })).then(entries => {
            if (mounted.current) setNames(prev => ({ ...prev, ...Object.fromEntries(entries) }));
        });
    }, [checks]);

    return useCallback(
        (buildingKey: string | undefined, componentKey: string) =>
            (buildingKey ? names[refKey(buildingKey, componentKey)] : undefined) ?? undefined,
        [names]
    );
};
